import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, RefreshCw } from 'lucide-react';
import { hubspotAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

const fmt = (s) => {
  if (!s) return '';
  try { return new Date(s).toLocaleString(); } catch { return String(s); }
};

/**
 * KR-only HubSpot sync panel for an agent: shows sync status, last sync
 * time, the HubSpot contact id / last error, and a manual re-sync button.
 */
export function HubspotSyncCard({ npn }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await hubspotAPI.agentSyncStatus(npn);
      setStatus(r.data);
    } catch {
      setStatus(null);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (npn) load(); /* eslint-disable-next-line */ }, [npn]);

  const sync = async () => {
    setSyncing(true);
    try {
      await hubspotAPI.triggerAgentSync(npn);
      toast.success('Synced to HubSpot');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'HubSpot sync failed');
      await load();
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-6 text-slate-400" data-testid="hubspot-sync-loading">
        <Loader2 size={16} className="animate-spin" />
      </div>
    );
  }

  const st = status?.hubspot_sync_status;
  return (
    <div className="space-y-3" data-testid="hubspot-sync-card">
      <div className="flex items-center justify-between">
        <span className="text-xs text-slate-500">Status</span>
        {st === 'synced' ? (
          <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100" data-testid="hubspot-status-badge">Synced</Badge>
        ) : st === 'error' ? (
          <Badge variant="destructive" data-testid="hubspot-status-badge">Error</Badge>
        ) : (
          <Badge variant="secondary" data-testid="hubspot-status-badge">Never Synced</Badge>
        )}
      </div>
      {status?.hubspot_last_sync && (
        <div className="flex items-center justify-between">
          <span className="text-xs text-slate-500">Last Sync</span>
          <span className="text-xs text-slate-700" data-testid="hubspot-last-sync">{fmt(status.hubspot_last_sync)}</span>
        </div>
      )}
      {status?.hubspot_contact_id && (
        <div className="flex items-center justify-between">
          <span className="text-xs text-slate-500">Contact ID</span>
          <span className="text-xs font-mono text-slate-700" data-testid="hubspot-contact-id">{status.hubspot_contact_id}</span>
        </div>
      )}
      {status?.hubspot_sync_error && (
        <div className="p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700 break-words" data-testid="hubspot-sync-error">
          {String(status.hubspot_sync_error).substring(0, 200)}
        </div>
      )}
      <Button
        variant="outline"
        size="sm"
        className="w-full"
        onClick={sync}
        disabled={syncing}
        data-testid="hubspot-sync-btn"
      >
        {syncing ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <RefreshCw size={14} className="mr-1.5" />}
        {syncing ? 'Syncing…' : 'Sync to HubSpot'}
      </Button>
    </div>
  );
}

export default HubspotSyncCard;
